import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Spinner, Alert, Form, Accordion, ProgressBar, Button, Badge, ListGroup } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { colors } from '../../brand-colors';

/**
 * Amazon Publishing Checklist
 * Interactive wizard to help users publish their books on Amazon KDP
 */
function AmazonPublishing() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [sections, setSections] = useState([]);
  const [completedItems, setCompletedItems] = useState([]);
  const [progress, setProgress] = useState({ completed: 0, total: 0, percent: 0 });

  // Book info
  const [bookTitle, setBookTitle] = useState('');
  const [bookSubtitle, setBookSubtitle] = useState('');
  const [kindleIsbn, setKindleIsbn] = useState('');
  const [paperbackIsbn, setPaperbackIsbn] = useState('');

  // Track which accordion is open
  const [activeKey, setActiveKey] = useState('0');

  useEffect(() => {
    loadChecklist();
  }, []);

  const loadChecklist = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/amazon-publishing/checklist', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSections(data.sections);
          setCompletedItems(data.completedItems || []);
          setProgress(data.progress);
          setBookTitle(data.bookTitle || '');
          setBookSubtitle(data.bookSubtitle || '');
          setKindleIsbn(data.kindleIsbn || '');
          setPaperbackIsbn(data.paperbackIsbn || '');

          // Open first incomplete section
          const firstIncomplete = data.sections.findIndex(section =>
            section.items.some(item => !data.completedItems.includes(item.id))
          );
          if (firstIncomplete >= 0) {
            setActiveKey(String(firstIncomplete));
          }
        }
      }
    } catch (err) {
      console.error('Error loading checklist:', err);
      setError('Failed to load checklist');
    } finally {
      setLoading(false);
    }
  };

  const toggleItem = async (itemId) => {
    try {
      const response = await fetch('/api/amazon-publishing/checklist/toggle', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_id: itemId })
      });

      const data = await response.json();
      if (data.success) {
        setCompletedItems(data.completedItems);
        setProgress(data.progress);
      }
    } catch (err) {
      console.error('Error toggling item:', err);
    }
  };

  const saveBookInfo = async () => {
    setSaving(true);
    try {
      const response = await fetch('/api/amazon-publishing/checklist/save', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bookTitle,
          bookSubtitle,
          kindleIsbn,
          paperbackIsbn
        })
      });

      const data = await response.json();
      if (!data.success) {
        setError(data.error || 'Failed to save');
      }
    } catch (err) {
      console.error('Error saving:', err);
      setError('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const getSectionProgress = (section) => {
    const completed = section.items.filter(item => completedItems.includes(item.id)).length;
    return `${completed}/${section.items.length}`;
  };

  const isSectionComplete = (section) => {
    return section.items.every(item => completedItems.includes(item.id));
  };

  if (loading) {
    return (
      <Container fluid className="py-4 px-4">
        <div className="text-center py-5">
          <Spinner animation="border" />
          <p className="mt-2">Loading checklist...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4 px-4">
      <div className="mb-4">
        <Link to="/command-center/authority-engineer" className="text-decoration-none mb-2 d-inline-block">
          ← Back to Authority Engineer
        </Link>
        <h1>Publish to Amazon</h1>
        <p className="text-muted">Step-by-step checklist to publish your book on Amazon KDP</p>
      </div>

      <Row>
        <Col lg={8}>
          {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

          {/* Progress Overview */}
          <Card className="shadow mb-4">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="mb-0">Your Progress</h5>
                <span className="text-muted">{progress.completed} of {progress.total} tasks complete</span>
              </div>
              <ProgressBar
                now={progress.percent}
                label={`${progress.percent}%`}
                variant={progress.percent === 100 ? 'success' : 'primary'}
                style={{ height: '25px' }}
              />
            </Card.Body>
          </Card>

          {/* Checklist Sections */}
          <Accordion activeKey={activeKey} onSelect={(k) => setActiveKey(k)}>
            {sections.map((section, idx) => (
              <Accordion.Item eventKey={String(idx)} key={section.id}>
                <Accordion.Header>
                  <div className="d-flex justify-content-between align-items-center w-100 me-3">
                    <div className="d-flex align-items-center">
                      {isSectionComplete(section) ? (
                        <span className="me-2" style={{ color: '#28a745', fontSize: '1.2rem' }}>✓</span>
                      ) : (
                        <span className="me-2" style={{ color: '#6c757d', fontSize: '1.2rem' }}>○</span>
                      )}
                      <div>
                        <strong>{section.title}</strong>
                        <small className="text-muted d-block">{section.description}</small>
                      </div>
                    </div>
                    <span className={`badge ${isSectionComplete(section) ? 'bg-success' : 'bg-secondary'}`}>
                      {getSectionProgress(section)}
                    </span>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  {section.notes && (
                    <Alert variant="info" className="mb-3">
                      <small>{section.notes}</small>
                    </Alert>
                  )}
                  <div className="list-group list-group-flush">
                    {section.items.map((item) => (
                      <div
                        key={item.id}
                        className="list-group-item d-flex align-items-center px-0"
                        style={{ cursor: 'pointer' }}
                        onClick={() => toggleItem(item.id)}
                      >
                        <Form.Check
                          type="checkbox"
                          checked={completedItems.includes(item.id)}
                          onChange={() => toggleItem(item.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="me-3"
                          style={{ transform: 'scale(1.3)' }}
                        />
                        <span style={{
                          textDecoration: completedItems.includes(item.id) ? 'line-through' : 'none',
                          color: completedItems.includes(item.id) ? '#6c757d' : 'inherit'
                        }}>
                          {item.text}
                        </span>
                      </div>
                    ))}
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Col>

        {/* Sidebar */}
        <Col lg={4}>
          {/* Book Info */}
          <Card className="shadow mb-4">
            <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
              <h6 className="mb-0">Your Book</h6>
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>Book Title</Form.Label>
                <Form.Control
                  type="text"
                  value={bookTitle}
                  onChange={(e) => setBookTitle(e.target.value)}
                  onBlur={saveBookInfo}
                  placeholder="Enter your book title"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Subtitle (optional)</Form.Label>
                <Form.Control
                  type="text"
                  value={bookSubtitle}
                  onChange={(e) => setBookSubtitle(e.target.value)}
                  onBlur={saveBookInfo}
                  placeholder="Enter subtitle"
                />
              </Form.Group>
              <hr />
              <Form.Group className="mb-3">
                <Form.Label>Kindle ISBN</Form.Label>
                <Form.Control
                  type="text"
                  value={kindleIsbn}
                  onChange={(e) => setKindleIsbn(e.target.value)}
                  onBlur={saveBookInfo}
                  placeholder="978-..."
                />
              </Form.Group>
              <Form.Group className="mb-0">
                <Form.Label>Paperback ISBN</Form.Label>
                <Form.Control
                  type="text"
                  value={paperbackIsbn}
                  onChange={(e) => setPaperbackIsbn(e.target.value)}
                  onBlur={saveBookInfo}
                  placeholder="978-..."
                />
              </Form.Group>
              {saving && <small className="text-muted mt-2 d-block">Saving...</small>}
            </Card.Body>
          </Card>

          {/* Quick Links */}
          <Card className="shadow mb-4">
            <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
              <h6 className="mb-0">Useful Links</h6>
            </Card.Header>
            <Card.Body className="p-3">
              <div className="d-grid gap-2">
                <a
                  href="https://kdp.amazon.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-dark btn-sm"
                >
                  Amazon KDP Dashboard
                </a>
                <a
                  href="https://www.myidentifiers.com/identify-protect-your-book/isbn/buy-isbn"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-dark btn-sm"
                >
                  Buy ISBNs (Bowker)
                </a>
                <a
                  href="https://locexternal.servicenowservices.com/auth"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-dark btn-sm"
                >
                  LCCN Registration (Library of Congress)
                </a>
                <a
                  href="https://authorcentral.amazon.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-dark btn-sm"
                >
                  Author Central
                </a>
              </div>
            </Card.Body>
          </Card>

          {/* Trim Sizes Reference */}
          <Card className="shadow mb-4">
            <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
              <h6 className="mb-0">Trim Sizes</h6>
            </Card.Header>
            <Card.Body className="p-3">
              <div className="mb-3">
                <strong className="d-block mb-1" style={{ color: '#28a745' }}>Most Popular:</strong>
                <small className="text-muted">
                  5" x 8" &bull; 5.25" x 8" &bull; 5.5" x 8.5" &bull; 6" x 9"
                </small>
              </div>
              <div className="mb-2">
                <strong className="d-block mb-1">Standard Sizes:</strong>
                <small className="text-muted">
                  5.06" x 7.81" &bull; 6.14" x 9.21" &bull; 6.69" x 9.61" &bull; 7" x 10" &bull; 7.44" x 9.69" &bull; 7.5" x 9.25" &bull; 8" x 10" &bull; 8.5" x 11"
                </small>
              </div>
              <Alert variant="warning" className="mb-0 py-2 px-2">
                <small>Other sizes cost more and have limited distribution options.</small>
              </Alert>
            </Card.Body>
          </Card>

          {/* Spine Calculator */}
          <Card className="shadow mb-4">
            <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
              <h6 className="mb-0">Spine Width Calculator</h6>
            </Card.Header>
            <Card.Body>
              <SpineCalculator />
            </Card.Body>
          </Card>

          {/* Category Suggester */}
          <Card className="shadow mb-4">
            <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
              <h6 className="mb-0">Category Suggester</h6>
            </Card.Header>
            <Card.Body>
              <CategorySuggester bookTitle={bookTitle} bookSubtitle={bookSubtitle} />
            </Card.Body>
          </Card>

          {/* Keyword Generator */}
          <Card className="shadow">
            <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
              <h6 className="mb-0">Keyword Generator</h6>
            </Card.Header>
            <Card.Body>
              <KeywordGenerator bookTitle={bookTitle} bookSubtitle={bookSubtitle} />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

/**
 * Mini spine width calculator component
 */
function SpineCalculator() {
  const [pageCount, setPageCount] = useState('');
  const spineWidth = pageCount ? (parseFloat(pageCount) * 0.002252).toFixed(3) : null;

  return (
    <div>
      <Form.Group className="mb-3">
        <Form.Label>Page Count</Form.Label>
        <Form.Control
          type="number"
          value={pageCount}
          onChange={(e) => setPageCount(e.target.value)}
          placeholder="e.g., 200"
        />
      </Form.Group>
      {spineWidth && (
        <Alert variant="success" className="mb-0">
          <strong>Spine Width:</strong> {spineWidth}" ({(parseFloat(spineWidth) * 25.4).toFixed(2)}mm)
        </Alert>
      )}
      <small className="text-muted d-block mt-2">
        Formula: pages × 0.002252" (for white paper)
      </small>
    </div>
  );
}

/**
 * Category Suggester - AI-powered Amazon category recommendations
 */
function CategorySuggester({ bookTitle, bookSubtitle }) {
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [error, setError] = useState('');

  const generateSuggestions = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch('/api/authority-engineer/amazon/suggest-categories', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ book_title: bookTitle, book_subtitle: bookSubtitle })
      });
      const data = await response.json();
      if (data.success) {
        setSuggestions(data.suggestions);
      } else {
        setError(data.error || 'Failed to generate suggestions');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <p className="small text-muted mb-3">
        Get AI-powered category suggestions based on your book and IP.
      </p>
      <Button
        variant="dark"
        size="sm"
        onClick={generateSuggestions}
        disabled={loading}
        className="w-100 mb-3"
        style={{ backgroundColor: colors.greyDarkest }}
      >
        {loading ? <><Spinner size="sm" className="me-2" />Analyzing...</> : 'Suggest Categories'}
      </Button>

      {error && <Alert variant="danger" className="py-2">{error}</Alert>}

      {suggestions && (
        <div>
          {suggestions.primary_categories?.length > 0 && (
            <div className="mb-3">
              <strong className="d-block mb-2" style={{ fontSize: '0.85rem' }}>
                <Badge bg="success" className="me-2">Primary</Badge>
                BISAC Categories
              </strong>
              <ListGroup variant="flush" className="small">
                {suggestions.primary_categories.map((cat, idx) => (
                  <ListGroup.Item key={idx} className="px-0 py-2">
                    <div className="fw-bold">{cat.path}</div>
                    <small className="text-muted">{cat.reason}</small>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </div>
          )}

          {suggestions.browse_categories?.length > 0 && (
            <div className="mb-3">
              <strong className="d-block mb-2" style={{ fontSize: '0.85rem' }}>
                <Badge bg="info" className="me-2">Browse</Badge>
                Additional Categories
              </strong>
              <ListGroup variant="flush" className="small">
                {suggestions.browse_categories.slice(0, 5).map((cat, idx) => (
                  <ListGroup.Item key={idx} className="px-0 py-2">
                    <div className="fw-bold">{cat.path}</div>
                    <small className="text-muted">{cat.reason}</small>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </div>
          )}

          {suggestions.strategy_notes && (
            <Alert variant="light" className="py-2 small">
              <strong>Strategy:</strong> {suggestions.strategy_notes}
            </Alert>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Keyword Generator - AI-powered Amazon keyword suggestions
 */
function KeywordGenerator({ bookTitle, bookSubtitle }) {
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(null);

  const generateSuggestions = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch('/api/authority-engineer/amazon/suggest-keywords', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ book_title: bookTitle, book_subtitle: bookSubtitle })
      });
      const data = await response.json();
      if (data.success) {
        setSuggestions(data.suggestions);
      } else {
        setError(data.error || 'Failed to generate suggestions');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const copyKeyword = (keyword, idx) => {
    navigator.clipboard.writeText(keyword);
    setCopied(idx);
    setTimeout(() => setCopied(null), 1500);
  };

  const getVolumeBadge = (volume) => {
    const variants = { high: 'success', medium: 'warning', low: 'secondary' };
    return <Badge bg={variants[volume] || 'secondary'} className="ms-2">{volume}</Badge>;
  };

  const getTypeBadge = (type, slot) => {
    if (type === 'core' || slot <= 3) {
      return <Badge bg="primary" className="ms-2">Core</Badge>;
    }
    return <Badge bg="info" className="ms-2">Long-tail</Badge>;
  };

  return (
    <div>
      <p className="small text-muted mb-3">
        Get 7 optimized keywords using the proven long-tail strategy for better discoverability.
      </p>
      <Button
        variant="dark"
        size="sm"
        onClick={generateSuggestions}
        disabled={loading}
        className="w-100 mb-3"
        style={{ backgroundColor: colors.greyDarkest }}
      >
        {loading ? <><Spinner size="sm" className="me-2" />Analyzing...</> : 'Generate Keywords'}
      </Button>

      {error && <Alert variant="danger" className="py-2">{error}</Alert>}

      {suggestions && (
        <div>
          {/* Strategy explanation */}
          <Alert variant="info" className="py-2 small mb-3">
            <strong>Strategy:</strong> Slots 1-3 are <Badge bg="primary">Core</Badge> keywords you want to rank for.
            Slots 4-7 are <Badge bg="info">Long-tail</Badge> (4-6 words) for easier discovery.
          </Alert>

          {suggestions.keywords?.length > 0 && (
            <div className="mb-3">
              <strong className="d-block mb-2" style={{ fontSize: '0.85rem' }}>
                Your 7 Keywords <small className="text-muted">(click to copy)</small>
              </strong>
              <ListGroup variant="flush" className="small">
                {suggestions.keywords.map((kw, idx) => (
                  <ListGroup.Item
                    key={idx}
                    className="px-0 py-2 d-flex justify-content-between align-items-center"
                    style={{ cursor: 'pointer' }}
                    onClick={() => copyKeyword(kw.keyword, idx)}
                  >
                    <div>
                      <span className="text-muted me-2">#{kw.slot || idx + 1}</span>
                      <span className="fw-bold">{kw.keyword}</span>
                      {getTypeBadge(kw.type, kw.slot || idx + 1)}
                      {kw.search_volume && getVolumeBadge(kw.search_volume)}
                      <small className="text-muted d-block ms-4">{kw.reason}</small>
                    </div>
                    <span className="text-muted">
                      {copied === idx ? '✓' : `${kw.keyword.length}/50`}
                    </span>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </div>
          )}

          {suggestions.bonus_suggestions?.length > 0 && (
            <div className="mb-3">
              <strong className="d-block mb-2" style={{ fontSize: '0.85rem' }}>Bonus Ideas</strong>
              <div className="small">
                {suggestions.bonus_suggestions.map((kw, idx) => (
                  <Badge
                    key={idx}
                    bg="light"
                    text="dark"
                    className="me-1 mb-1"
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      navigator.clipboard.writeText(kw);
                      setCopied(`bonus-${idx}`);
                      setTimeout(() => setCopied(null), 1500);
                    }}
                  >
                    {copied === `bonus-${idx}` ? '✓ Copied' : kw}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {suggestions.avoid?.length > 0 && (
            <Alert variant="warning" className="py-2 small mb-2">
              <strong>Avoid:</strong> {suggestions.avoid.join(', ')}
            </Alert>
          )}

          {suggestions.strategy_notes && (
            <Alert variant="light" className="py-2 small">
              <strong>Strategy:</strong> {suggestions.strategy_notes}
            </Alert>
          )}
        </div>
      )}
    </div>
  );
}

export default AmazonPublishing;
